import { ApolloClient, InMemoryCache, ApolloLink, fromPromise } from '@apollo/client';
import { createHttpLink } from '@apollo/client/link/http';
import { onError } from '@apollo/client/link/error';
import { gql } from '@apollo/client';

const REFRESH_TOKEN_MUTATION = gql`
  mutation RefreshToken($token: String) {
    refreshToken(token: $token) {
      token
      refreshToken
    }
  }
`;

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URL || 'http://localhost:8000/graphql/',
  credentials: 'include',
});

const authLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem('token');

  operation.setContext({
    headers: {
      authorization: token ? `JWT ${token}` : '',
    },
  });

  return forward(operation);
});

let isRefreshing = false;
let pendingRequests = [];

const resolvePending = () => {
  pendingRequests.forEach((cb) => cb());
  pendingRequests = [];
};

const REFRESH_BACKOFF_MS = [5000, 15000, 30000];
const REFRESH_MAX_RETRIES = 3;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const hardLogout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

// Attempt refresh with retry/backoff for network errors.
// Resolves with a new access token on success.
// Rejects only when the refresh token is explicitly rejected by the
// backend (GraphQL auth error). Network errors are retried up to
// REFRESH_MAX_RETRIES times, then reject so the caller can decide.
const refreshWithRetry = async (storedRefreshToken) => {
  let lastError = null;
  for (let i = 0; i < REFRESH_MAX_RETRIES; i++) {
    try {
      const { data } = await client.mutate({
        mutation: REFRESH_TOKEN_MUTATION,
        variables: { token: storedRefreshToken },
      });
      if (data?.refreshToken?.token) {
        const newToken = data.refreshToken.token;
        const newRefresh = data.refreshToken.refreshToken || storedRefreshToken;
        localStorage.setItem('token', newToken);
        localStorage.setItem('refreshToken', newRefresh);
        return newToken;
      }
      // Explicit reject from backend (refresh token invalid/expired).
      const err = new Error('Refresh token rejected');
      err.hardReject = true;
      throw err;
    } catch (err) {
      lastError = err;
      if (err.hardReject) throw err;
      if (i < REFRESH_MAX_RETRIES - 1) {
        await sleep(REFRESH_BACKOFF_MS[i]);
      }
    }
  }
  throw lastError;
};

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  // Narrow auth-error detection: only JWT signature/expiry errors.
  // 'Not authenticated' alone is too broad and triggers spurious
  // refresh attempts on unrelated auth failures.
  const authError =
    graphQLErrors &&
    graphQLErrors.some(
      (err) =>
        err.message.includes('Signature has expired') ||
        err.message.includes('Error decoding signature')
    );

  if (authError) {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      hardLogout();
      return;
    }

    if (!isRefreshing) {
      isRefreshing = true;

      return fromPromise(
        refreshWithRetry(refreshToken)
          .then((newToken) => {
            const oldHeaders = operation.getContext().headers;
            operation.setContext({
              headers: {
                ...oldHeaders,
                authorization: `JWT ${newToken}`,
              },
            });
            resolvePending();
          })
          .catch((err) => {
            pendingRequests = [];
            if (err && err.hardReject) {
              hardLogout();
              return;
            }
            // Network errors exhausted retries → preserve session,
            // don't redirect. Original operation will fail with the
            // network error and the UI can surface it.
          })
          .finally(() => {
            isRefreshing = false;
          })
      ).flatMap(() => forward(operation));
    }

    // Queue pending operations while refreshing
    return fromPromise(
      new Promise((resolve) => {
        pendingRequests.push(() => resolve());
      })
    ).flatMap(() => {
      const oldHeaders = operation.getContext().headers;
      const currentToken = localStorage.getItem('token');
      operation.setContext({
        headers: {
          ...oldHeaders,
          authorization: currentToken ? `JWT ${currentToken}` : '',
        },
      });
      return forward(operation);
    });
  }
});

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

export default client;
